import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Calendar,
  Clock3,
  Layers3,
  Plus,
  ReceiptText,
  Wallet,
  Zap,
} from 'lucide-react';
import { api } from '../api';
import type { MatchEntry, Party, PartyBalance, WeeklyBatch } from '../api';
import { AppShell, useReadOnly } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { ActionQueueCard, BatchTicket, MetricStrip, TimelineRow } from '../ui/mobileOps';
import { Btn, EmptyState, Money, RowSkeleton, SectionCard, StatePill } from '../ui/components';

interface MatchBalance {
  matchAccountId: number;
  balance: string;
}

export function DashboardPage() {
  const nav = useNavigate();
  const readOnly = useReadOnly();

  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const batchesQ = useQuery<WeeklyBatch[]>({
    queryKey: ['batches'],
    queryFn: () => api.get<WeeklyBatch[]>('/api/batches'),
  });
  const matchEntriesQ = useQuery<MatchEntry[]>({
    queryKey: ['match-entries-recent'],
    queryFn: () => api.get<MatchEntry[]>('/api/match/entries?limit=8'),
  });
  const matchBalancesQ = useQuery<MatchBalance[]>({
    queryKey: ['match-balances'],
    queryFn: () => api.get<MatchBalance[]>('/api/match/balances'),
  });

  const parties = partiesQ.data ?? [];
  const ledgerParties = useMemo(
    () => parties.filter((party) => party.role !== 'self' && party.status === 'active'),
    [parties],
  );
  const balanceQueries = useQueries({
    queries: ledgerParties.map((party) => ({
      queryKey: ['balance', party.id],
      queryFn: () => api.get<PartyBalance>(`/api/parties/${party.id}/balance`),
      staleTime: 30_000,
    })),
  });

  const recentEntries = useMemo(() => (matchEntriesQ.data ?? []).slice(0, 6), [matchEntriesQ.data]);
  const recentBatches = useMemo(() => (batchesQ.data ?? []).slice(0, 4), [batchesQ.data]);
  const partyById = useMemo(() => new Map(parties.map((p) => [p.id, p])), [parties]);

  const stats = useMemo(() => {
    const batches = batchesQ.data ?? [];
    const open = batches.filter((batch) => batch.state === 'draft' || batch.state === 'verified');
    const draftCount = open.filter((batch) => batch.state === 'draft').length;
    const verifiedCount = open.filter((batch) => batch.state === 'verified').length;
    const totalMatchBalance = (matchBalancesQ.data ?? []).reduce(
      (sum, entry) => sum + Number(entry.balance || '0'),
      0,
    );

    const exposures = ledgerParties
      .map((party, index) => {
        const balance = balanceQueries[index]?.data?.balance ?? '0';
        return {
          party,
          balance,
          abs: Math.abs(Number(balance || '0')),
        };
      })
      .sort((a, b) => b.abs - a.abs);

    return {
      partyCount: ledgerParties.length,
      openCount: open.length,
      draftCount,
      verifiedCount,
      totalMatchBalance,
      biggestExposure: exposures[0] ?? null,
      exposures,
    };
  }, [batchesQ.data, balanceQueries, ledgerParties, matchBalancesQ.data]);

  const loading =
    partiesQ.isLoading ||
    batchesQ.isLoading ||
    matchEntriesQ.isLoading ||
    matchBalancesQ.isLoading ||
    balanceQueries.some((query) => query.isLoading);

  const queueTitle =
    stats.draftCount > 0
      ? `Review ${stats.draftCount} draft batch${stats.draftCount === 1 ? '' : 'es'}`
      : stats.verifiedCount > 0
      ? `Post ${stats.verifiedCount} verified batch${stats.verifiedCount === 1 ? '' : 'es'}`
      : 'Everything posted';

  const queueDescription =
    stats.draftCount > 0
      ? 'Start with draft settlement review.'
      : stats.verifiedCount > 0
      ? 'Verified batches are ready for posting.'
      : 'The queue is clear. Record the next movement when it lands.';

  return (
    <AppShell
      title="Home"
      toolbar={
        <PageHeader
          section="dashboard"
          title="Home"
          subtitle="Today, work queue, and recent movement"
          actions={
            !readOnly ? (
              <>
                <Btn size="sm" onClick={() => nav('/batches/new')}>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span>New batch</span>
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => nav('/match/entry')}>
                  <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <span>Quick entry</span>
                </Btn>
              </>
            ) : undefined
          }
        />
      }
    >
      <div className="flex flex-col gap-4">
        <div className="order-2 md:order-1">
          {!loading && (
            <MetricStrip
            section="dashboard"
            columns={4}
            items={[
              {
                label: 'Open work',
                value: String(stats.openCount),
                note: stats.draftCount > 0 ? `${stats.draftCount} draft` : 'queue clear',
                to: '/batches',
              },
              {
                label: 'Parties',
                value: String(stats.partyCount),
                note: 'active ledgers',
                to: '/ledgers',
              },
              {
                label: 'Match net',
                value: stats.totalMatchBalance.toFixed(2),
                note: 'recent account flow',
                to: '/match',
                tone: stats.totalMatchBalance < 0 ? 'neg' : stats.totalMatchBalance > 0 ? 'pos' : 'default',
              },
              {
                label: 'Largest balance',
                value: stats.biggestExposure?.balance ?? '0',
                note: stats.biggestExposure?.party.displayName ?? 'no exposure',
                to: stats.biggestExposure ? `/parties/${stats.biggestExposure.party.id}/ledger` : '/ledgers',
                tone:
                  Number(stats.biggestExposure?.balance ?? '0') < 0
                    ? 'neg'
                    : Number(stats.biggestExposure?.balance ?? '0') > 0
                    ? 'pos'
                    : 'default',
              },
            ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2 grid gap-3 lg:grid-cols-[1.15fr_0.85fr]">
          <ActionQueueCard
            section="dashboard"
            eyebrow="Today"
            title={queueTitle}
            description={queueDescription}
            meta={
              stats.openCount > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-raised px-2.5 py-1 text-[11px] font-medium text-ink-muted">
                    <Clock3 className="h-3.5 w-3.5" strokeWidth={2} />
                    {stats.openCount} open queue item{stats.openCount === 1 ? '' : 's'}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-surface-raised px-2.5 py-1 text-[11px] font-medium text-ink-muted">
                    <Layers3 className="h-3.5 w-3.5" strokeWidth={2} />
                    {recentEntries.length} recent match entries
                  </span>
                </div>
              ) : (
                'Use Quick Entry or start a new batch when the next sheet arrives.'
              )
            }
            actions={
              <>
                <Btn size="sm" onClick={() => nav('/batches')}>
                  Open queue
                </Btn>
                {!readOnly && (
                  <Btn size="sm" variant="secondary" onClick={() => nav('/batches/new')}>
                    Start batch
                  </Btn>
                )}
              </>
            }
          />

          <ActionQueueCard
            section="dashboard"
            eyebrow="Quick actions"
            title="Open the flow you use most"
            description="Start a batch, add a match entry, or jump straight into ledgers and cash."
            tone="neutral"
            actions={
              <>
                {!readOnly && (
                  <Btn size="sm" onClick={() => nav('/match/entry')}>
                    Match entry
                  </Btn>
                )}
                <Btn size="sm" variant="secondary" onClick={() => nav('/ledgers')}>
                  Open ledgers
                </Btn>
                <Btn size="sm" variant="secondary" onClick={() => nav('/cash')}>
                  Open cash
                </Btn>
              </>
            }
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <SectionCard
            section="batches"
            title="Weekly queue"
            action={
              <Link to="/batches" className="text-caption font-medium text-sec-batches hover:underline">
                All batches
              </Link>
            }
          >
            {batchesQ.isLoading ? (
              <RowSkeleton rows={4} />
            ) : recentBatches.length === 0 ? (
              <EmptyState
                section="batches"
                icon={Calendar}
                title="No batches yet"
                description="Create a batch when the weekly P&L sheet comes in."
                action={
                  !readOnly ? (
                    <Btn size="sm" onClick={() => nav('/batches/new')}>
                      New batch
                    </Btn>
                  ) : undefined
                }
              />
            ) : (
              <div className="space-y-3">
                {recentBatches.map((batch) => {
                  const partyName = partyById.get(batch.uplinePartyId)?.displayName ?? `Party #${batch.uplinePartyId}`;
                  return (
                    <BatchTicket
                      key={batch.id}
                      to={`/batches/${batch.id}`}
                      week={batch.weekMonday}
                      title={partyName}
                      state={batch.state}
                      detail={`Batch #${batch.id}`}
                      note={`Created ${batch.createdAt.slice(0, 10)}`}
                      footer={
                        batch.state === 'draft'
                          ? 'Needs review'
                          : batch.state === 'verified'
                          ? 'Ready to post'
                          : 'Already posted'
                      }
                    />
                  );
                })}
              </div>
            )}
          </SectionCard>

          <SectionCard
            section="match"
            title="Recent movement"
            action={
              <Link to="/match" className="text-caption font-medium text-sec-match hover:underline">
                Match section
              </Link>
            }
          >
            {matchEntriesQ.isLoading ? (
              <RowSkeleton rows={5} />
            ) : recentEntries.length === 0 ? (
              <EmptyState
                section="match"
                icon={ReceiptText}
                title="No movement yet"
                description="Recent match entries appear here so you can jump back into the exact account or note."
              />
            ) : (
              <div className="space-y-3">
                {recentEntries.map((entry) => (
                  <Link key={entry.id} to={`/match/entries/${entry.id}`} className="block">
                    <TimelineRow
                      section="match"
                      title={entry.note?.trim() || 'Match entry'}
                      subtitle={entry.voidedAt ? 'Voided entry' : 'Signed amount posted'}
                      date={entry.effectiveDate}
                      amount={<Money amount={entry.amount} className="font-mono text-small font-semibold" />}
                      amountTone={
                        Number(entry.amount || '0') < 0 ? 'neg' : Number(entry.amount || '0') > 0 ? 'pos' : 'default'
                      }
                      badges={entry.voidedAt ? <StatePill state="archived" /> : undefined}
                      action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                    />
                  </Link>
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <SectionCard
          section="ledgers"
          title="Balances to watch"
          action={
            <Link to="/ledgers" className="text-caption font-medium text-sec-ledgers hover:underline">
              All ledgers
            </Link>
          }
        >
          {loading ? (
            <RowSkeleton rows={5} />
          ) : stats.exposures.length === 0 ? (
            <EmptyState
              section="ledgers"
              icon={Wallet}
              title="No balances yet"
              description="Ledger balances will show here as soon as settlements or manual entries start moving."
            />
          ) : (
            <div className="space-y-3">
              {stats.exposures.slice(0, 5).map((entry) => (
                <Link key={entry.party.id} to={`/parties/${entry.party.id}/ledger`} className="block">
                  <TimelineRow
                    section="ledgers"
                    title={entry.party.displayName}
                    subtitle={entry.party.role}
                    date="Current balance"
                    amount={entry.balance}
                    amountTone={Number(entry.balance || '0') < 0 ? 'neg' : Number(entry.balance || '0') > 0 ? 'pos' : 'default'}
                    action={<ArrowRight className="ml-auto h-4 w-4 text-ink-muted" strokeWidth={1.75} />}
                  />
                </Link>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
}
